import { useState, useEffect, useMemo } from "react";
import { MessageCircle, Mail, Copy, Check, Link as LinkIcon, Eye, Smartphone } from "lucide-react";
import Modal from "../components/Modal";
import Field, { selectStyle, inputStyle } from "../components/Field";
import BuscaParceiro from "../components/BuscaParceiro";
import QRCode from "../components/QRCode";
import { SISTEMAS, MODELOS_FICHA, LISTA_MODELOS_FICHA, copiaDaTabela } from "../constants";
import { EscolherTabela } from "../components/TabelasComerciais";
import { criarConvite } from "../lib/tabelasComerciais";
import { supabase } from "../lib/supabase";
import { encodeFichaToken, fichaFormUrl, whatsappUrl, emailUrl } from "../lib/fichaLink";
import { soDigitos } from "../lib/rede";

// Monta as linhas de cabeçalho conforme o modo escolhido
function buildHeader(mode, { uf, clientes, orgao, licitacao }) {
  if (mode === "cliente") {
    const l1 = uf ? `Atendimento à frota de clientes InstaSolutions — ${uf}` : "Atendimento à frota de clientes InstaSolutions";
    const l2 = clientes?.length ? `Clientes na região: ${clientes.slice(0, 8).join(", ")}${clientes.length > 8 ? "…" : ""}` : "";
    return { l1, l2 };
  }
  if (mode === "orgao") {
    if (!orgao && !licitacao) return null;
    return { l1: orgao ? `Órgão: ${orgao}` : "", l2: licitacao ? `Licitação: ${licitacao}` : "" };
  }
  return null; // sem cabeçalho
}

export default function SendFichaLink({ open, onClose, partners = [], clients = [], credenciamentos = [], userId, presetPartner = null }) {
  const [partnerId, setPartnerId] = useState("");
  const [modeloId, setModeloId] = useState("manutencao");
  const [sistemas, setSistemas] = useState(["manutencao"]);
  const [headerMode, setHeaderMode] = useState("none"); // none | cliente | orgao
  const [uf, setUf] = useState("");
  const [orgao, setOrgao] = useState("");
  const [licitacao, setLicitacao] = useState("");
  const [copied, setCopied] = useState(false);
  // Tabela comercial (combustível): o link leva só o id do convite — ver
  // lib/tabelasComerciais.js. `tabelaCod` "" = tabela padrão.
  const [tabelaCod, setTabelaCod] = useState("");
  const [conviteId, setConviteId] = useState(null);
  const [tabelaEstado, setTabelaEstado] = useState(""); // "" | "gerando" | "erro: …" | "ok"
  const [tabelaAtual, setTabelaAtual] = useState(null); // cópia escolhida (mostra/edita a exclusiva)

  useEffect(() => {
    if (!open) return;
    setPartnerId(presetPartner?.id || "");
    setCopied(false); setModeloId("manutencao"); setSistemas(["manutencao"]);
    setHeaderMode("none"); setUf(""); setOrgao(""); setLicitacao("");
    setTabelaCod(""); setConviteId(null); setTabelaEstado(""); setTabelaAtual(null);
  }, [open, presetPartner]);

  const partner = presetPartner || partners.find((p) => p.id === partnerId) || null;
  const nome = partner?.nomeFantasia || partner?.name || "";
  const phone = partner?.phone || "";

  // UFs presentes nos clientes + clientes daquele estado
  const ufOptions = useMemo(() => Array.from(new Set((clients || []).map((c) => c.region).filter(Boolean))).sort(), [clients]);
  const clientesUF = useMemo(() => (uf ? (clients || []).filter((c) => c.region === uf).map((c) => c.company).filter(Boolean) : []), [clients, uf]);

  const modelo = MODELOS_FICHA[modeloId];
  const header = buildHeader(headerMode, { uf, clientes: clientesUF, orgao, licitacao });

  // ── A empresa já tem ficha? Então o link é DELA ────────────────────
  //
  // Depois do "Aceitou credenciar" este modal abre sozinho, e montava sempre o
  // link de ficha NOVA. A empresa que nasceu no registro rápido já tinha
  // ficha: o fornecedor preenchia, o formulário dava INSERT, e o CRM ficava com
  // duas — a do registro rápido, vazia, e a preenchida, solta (foi assim com a
  // MECÂNICA G3 e o Auto Center Star Lava Jato). Agora, se existe ficha em
  // aberto desta empresa (pelo parceiro ou pelo CNPJ), o link reabre ESSA
  // ficha, pelo mesmo token do "Enviar p/ correção".
  const fichaExistente = useMemo(() => {
    if (!partner) return null;
    const cnpj = soDigitos(partner.cnpj);
    return credenciamentos.find((f) => f.partnerId === partner.id)
      || (cnpj.length === 14 ? credenciamentos.find((f) => soDigitos(f.cnpj) === cnpj) : null)
      || null;
  }, [partner, credenciamentos]);
  const reabre = !!fichaExistente?.editToken && fichaExistente.status !== "concluido";
  const jaConcluida = fichaExistente?.status === "concluido";
  // `t` = modelo do formulário (Manutenção / Combustível / Aeronáutico).
  const token = reabre
    ? encodeFichaToken({ c: fichaExistente.id, k: fichaExistente.editToken, s: sistemas, t: modeloId, n: nome || null })
    : encodeFichaToken({ p: partner?.id || null, u: userId || null, s: sistemas, t: modeloId, n: nome || null, h: header || undefined, tc: conviteId || undefined });

  // ── Tabela comercial ──────────────────────────────────────────────
  const ehCombustivel = modeloId === "combustivel" || sistemas.includes("combustivel") || sistemas.includes("abastecimento");
  const tabelaDaExistente = fichaExistente?.perfil?.tabelaComercial?.codigo || "";
  const aceitaJa = !!fichaExistente?.aceiteTermos;
  useEffect(() => {
    if (open && reabre) { setTabelaCod(tabelaDaExistente); setTabelaAtual(fichaExistente?.perfil?.tabelaComercial || null); }
  }, [open, reabre, tabelaDaExistente]); // eslint-disable-line
  const escolherTabela = async (linha) => {
    const cod = linha?.codigo || "";
    setTabelaCod(cod); setConviteId(null); setTabelaAtual(linha ? copiaDaTabela(linha) : null);
    if (!cod && !reabre) { setTabelaEstado(""); return; }
    setTabelaEstado("gerando");
    try {
      if (reabre) {
        // A ficha já existe: a tabela vai direto nela (o banco recusa se o
        // posto já aceitou — R-127).
        const perfil = { ...(fichaExistente.perfil || {}) };
        if (linha) perfil.tabelaComercial = copiaDaTabela(linha); else delete perfil.tabelaComercial;
        const { error } = await supabase.from("credenciamentos").update({ perfil }).eq("id", fichaExistente.id);
        if (error) throw new Error(error.message);
      } else {
        setConviteId(await criarConvite(linha, partner?.id || null));
      }
      setTabelaEstado("ok");
    } catch (e) {
      setTabelaCod(reabre ? tabelaDaExistente : ""); setConviteId(null);
      setTabelaEstado(`erro: ${e.message}`);
    }
  };
  // Enquanto o convite não volta, o link sem ele mandaria a tabela padrão.
  const linkPronto = tabelaEstado !== "gerando" && !(tabelaCod && !reabre && !conviteId);
  const url = fichaFormUrl(token);

  const toggleSis = (id) => setSistemas((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  // Trocar o modelo marca os sistemas de onde aquele fornecedor costuma entrar
  // (posto → Combustível; oficina de aeronave → Manutenção, onde fica o módulo
  // de aviação). Continua dando para ajustar os chips à mão depois.
  const escolherModelo = (id) => { setModeloId(id); setSistemas([...MODELOS_FICHA[id].sistemas]); };
  const copiar = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignora */ } };
  const abrirPrevia = () => window.open(`${url}&preview=1`, "_blank");

  const chip = (on) => ({ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569", fontWeight: on ? 600 : 400 });
  const modeBtn = (on) => ({ flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, textAlign: "center", border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff", color: on ? "#6d28d9" : "#475569" });
  const send = { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", borderRadius: 10, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#fff", flex: 1, textDecoration: "none" };

  return (
    <Modal open={open} onClose={onClose} title="Enviar formulário de credenciamento" w={560}>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14, lineHeight: 1.5 }}>
        O fornecedor preenche no <strong>próprio celular</strong> e tira a foto do documento pela câmera dele.
        O envio cria uma ficha <strong>pendente</strong> para sua equipe completar.
      </div>

      {/* Cada ramo tem o seu vocabulário: posto não responde "tipos de
          veículo", e oficina de aeronave precisa dizer o COM dela. */}
      <Field label="Formulário" help="Muda as perguntas e os nomes do formulário. Manutenção: oficinas da frota terrestre. Combustível: postos (dados ANP e pista). Aeronáutico: oficinas de aeronaves, com os requisitos da ANAC.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
          {LISTA_MODELOS_FICHA.map((m) => {
            const on = modeloId === m.id;
            return (
              <button key={m.id} type="button" onClick={() => escolherModelo(m.id)} style={{ textAlign: "left", padding: "9px 11px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", border: on ? "1.5px solid #7c3aed" : "1px solid #e2e8f0", background: on ? "#f5f3ff" : "#fff" }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: on ? "#6d28d9" : "#334155" }}>{m.label}</div>
                <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2, lineHeight: 1.35 }}>{m.descricao}</div>
              </button>
            );
          })}
        </div>
      </Field>

      {!presetPartner && (
        <Field label="Vincular a parceiro (opcional)" help="Digite parte do nome, do CNPJ ou da cidade. Vincular faz a ficha nascer amarrada à empresa da rede.">
          <BuscaParceiro partners={partners} value={partnerId} onChange={setPartnerId} />
        </Field>
      )}
      {presetPartner && <div style={{ fontSize: 12, color: "#334155", marginBottom: 12 }}>Parceiro: <strong>{nome}</strong></div>}

      {reabre && (
        <div style={{ marginBottom: 12, padding: "8px 11px", borderRadius: 8, background: "#ecfdf5", border: "1px solid #a7f3d0", fontSize: 11.5, color: "#065f46", lineHeight: 1.45 }}>
          Esta empresa já tem ficha. O link <strong>reabre essa mesma ficha</strong> — o que o fornecedor preencher cai nela, sem criar outra.
        </div>
      )}
      {jaConcluida && (
        <div style={{ marginBottom: 12, padding: "8px 11px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 11.5, color: "#991b1b", lineHeight: 1.45 }}>
          Esta empresa já tem uma ficha <strong>concluída</strong>. Mandar este formulário cria uma <strong>segunda ficha</strong>. Para pedir documento ou corrigir dado, use a própria ficha.
        </div>
      )}

      <Field label="Aparece nos sistemas" help="Muda o formulário e os termos: Combustível pergunta bandeira, combustíveis e registro na ANP; Manutenção pede o catálogo de serviços; Rastreamento, o socorro 24h e as rodovias.">
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {SISTEMAS.map((s) => <button key={s.id} type="button" onClick={() => toggleSis(s.id)} style={chip(sistemas.includes(s.id))}>{s.label}</button>)}
        </div>
      </Field>

      {ehCombustivel && (
        <EscolherTabela
          value={tabelaCod}
          atual={tabelaAtual}
          estado={fichaExistente?.estado || partner?.region || ""}
          nomePosto={nome}
          onEscolher={escolherTabela}
          disabled={tabelaEstado === "gerando" || (reabre && aceitaJa)}
          aviso={reabre && aceitaJa ? "Este posto já aceitou as condições — a tabela não muda (R-127)."
            : tabelaEstado === "gerando" ? "Preparando o link com esta tabela…"
            : tabelaEstado.startsWith("erro") ? `Não foi possível aplicar a tabela (${tabelaEstado.slice(6)}). O link segue com a anterior.`
            : tabelaCod && reabre ? "Gravada na ficha desta empresa: o link reabre com esta tabela."
            : tabelaCod ? "O link leva esta tabela; o posto não consegue trocá-la." : ""}
        />
      )}

      {/* Cabeçalho / contexto */}
      <Field label="Cabeçalho do formulário (opcional)" help="Em branco = sem cabeçalho. Você pode identificar os clientes que temos no estado que o fornecedor vai atender, ou o órgão/licitação.">
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          <div onClick={() => setHeaderMode("none")} style={modeBtn(headerMode === "none")}>Sem cabeçalho</div>
          <div onClick={() => setHeaderMode("cliente")} style={modeBtn(headerMode === "cliente")}>Cliente / Estado</div>
          <div onClick={() => setHeaderMode("orgao")} style={modeBtn(headerMode === "orgao")}>Órgão / Licitação</div>
        </div>
        {headerMode === "cliente" && (
          <div>
            <select style={selectStyle} value={uf} onChange={(e) => setUf(e.target.value)}>
              <option value="">Selecione o estado (UF)…</option>
              {ufOptions.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            {uf && (
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>
                {clientesUF.length ? <>{clientesUF.length} cliente(s) em {uf}: {clientesUF.slice(0, 6).join(", ")}{clientesUF.length > 6 ? "…" : ""}</> : `Nenhum cliente cadastrado em ${uf}.`}
              </div>
            )}
          </div>
        )}
        {headerMode === "orgao" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 10px" }}>
            <input style={inputStyle} value={orgao} onChange={(e) => setOrgao(e.target.value)} placeholder="Órgão (ex: Prefeitura de…)" />
            <input style={inputStyle} value={licitacao} onChange={(e) => setLicitacao(e.target.value)} placeholder="Licitação (ex: Pregão nº…)" />
          </div>
        )}
      </Field>

      {/* Prévia inline do que o fornecedor verá */}
      <div style={{ borderRadius: 10, border: "1px dashed #cbd5e1", background: "#f8fafc", padding: 12, marginTop: 4 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Prévia do formulário</div>
        {header && (
          <div style={{ borderRadius: 8, background: "#251C59", color: "#fff", padding: "10px 12px", marginBottom: 8 }}>
            {header.l1 && <div style={{ fontSize: 12, fontWeight: 700 }}>{header.l1}</div>}
            {header.l2 && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{header.l2}</div>}
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6d28d9", marginBottom: 4 }}>{modelo.titulo}</div>
        <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
          {modelo.previa.map((linha) => {
            const obrig = linha.endsWith(" *");
            return <div key={linha}>• {obrig ? linha.slice(0, -2) : linha}{obrig && <strong style={{ color: "#ef4444" }}> *</strong>}</div>;
          })}
          {modeloId === "combustivel" && sistemas.includes("rastreamento") && <div>• Socorro 24h / assistência em rodovias (o posto entra no rastreamento) <strong style={{ color: "#ef4444" }}>*</strong></div>}
        </div>
        <button onClick={abrirPrevia} style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 10, padding: "7px 12px", borderRadius: 7, border: "1px solid #7c3aed", background: "#fff", color: "#6d28d9", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <Eye size={13} /> Abrir prévia real
        </button>
      </div>

      {/* QR — o parceiro escaneia com o celular dele e preenche por lá */}
      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 14, padding: 14, borderRadius: 10, background: "#faf8ff", border: "1px solid #ddd6fe" }}>
        <QRCode value={url} size={140} style={{ flexShrink: 0, border: "1px solid #e9d5ff" }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#5b21b6" }}>
            <Smartphone size={15} /> Na visita: mostre este QR
          </div>
          <div style={{ fontSize: 12, color: "#6b21a8", lineHeight: 1.5, marginTop: 6 }}>
            O parceiro aponta a câmera do <strong>celular dele</strong> e preenche a ficha por lá — inclusive a foto
            do documento. Nada é anexado pelo tablet, então não trava nem perde o preenchimento.
          </div>
        </div>
      </div>

      {/* Link */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 12px", borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", marginTop: 10 }}>
        <LinkIcon size={14} color="#94a3b8" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{url}</span>
        <button onClick={copiar} disabled={!linkPronto} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: copied ? "#059669" : "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
          {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap", ...(linkPronto ? {} : { opacity: 0.45, pointerEvents: "none" }) }}>
        <a href={whatsappUrl(phone, token, nome)} target="_blank" rel="noreferrer" style={{ ...send, background: "#25D366" }}><MessageCircle size={16} /> WhatsApp</a>
        <a href={emailUrl(token, nome)} style={{ ...send, background: "#516BCC" }}><Mail size={16} /> E-mail</a>
      </div>
      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 10, textAlign: "center" }}>
        {phone ? "O WhatsApp abre já no contato do parceiro." : "Sem telefone do parceiro: o WhatsApp abre para você escolher o contato."}
      </div>
    </Modal>
  );
}
